import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "post-list-container" }
const _hoisted_2 = { class: "filter-section" }
const _hoisted_3 = { class: "search-wrapper" }
const _hoisted_4 = { class: "posts-container" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "post-header" }
const _hoisted_7 = { class: "post-author-info" }
const _hoisted_8 = { class: "post-time" }
const _hoisted_9 = { class: "user-actions" }
const _hoisted_10 = {
  key: 0,
  class: "post-cover"
}
const _hoisted_11 = ["src"]
const _hoisted_12 = { class: "post-content" }
const _hoisted_13 = { class: "post-title" }
const _hoisted_14 = { class: "post-text" }
const _hoisted_15 = { class: "post-actions" }
const _hoisted_16 = {
  key: 0,
  class: "user-actions"
}
const _hoisted_17 = {
  key: 1,
  class: "hot-comment"
}
const _hoisted_18 = { class: "comment-header" }
const _hoisted_19 = { class: "comment-author" }
const _hoisted_20 = { class: "comment-content" }
const _hoisted_21 = { class: "comment-time" }
const _hoisted_22 = { class: "pagination-container" }
const _hoisted_23 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_24 = {
  key: 1,
  class: "error-state"
}

import { ref, onMounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { postList, likePost as apiLikePost, favoritePost as apiFavoritePost, type PostItem, followUser, likePost, favoritePost, deletePost } from '@/user/api/post';
import { useStore } from 'vuex';
import { 
  ChatLineRound,
  Star,
  ThumbUp,
  Share
} from '@element-plus/icons-vue';
import { formatPostDate } from '@/utils/date';
import EditPostModal from './EditPostModal.vue'; // 引入编辑模态框组件

// 定义图标常量，供模板使用
interface Emits {
  'need-login': [];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PostListView',
  emits: ["need-login"],
  setup(__props, { emit: __emit }) {

// 临时从 vue 导入 defineEmits 以满足 ESLint 检查
const ThumbUpIcon = ThumbUp;
const ChatLineRoundIcon = ChatLineRound;
const StarIcon = Star;
const ShareIcon = Share;

// 定义 emits
const emit = __emit;

// 使用路由和状态管理
const router = useRouter();
const store = useStore();

// 计算属性获取当前登录用户的ID
const computedUserId = computed(() => {
  const userInfo = store.state.userInfo;
  return userInfo?.id || null;
});

// 添加点击帖子跳转到详情页的方法
const goToPostDetail = (postId: number) => {
  // 跳转到帖子详情页
  router.push(`/post/${postId}`);
};


// 响应式数据
const posts = ref<PostItem[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const totalRecords = ref(0);
const loading = ref(false);
const error = ref(false);
const errorMessage = ref('');
// 添加一个标志来跟踪是否有请求正在进行
const isRequestPending = ref(false);

// 搜索关键词
const searchKeyword = ref('');

// 编辑模态框相关状态
const editDialogVisible = ref(false);
const editingPostId = ref<number | null>(null);

// 帖子列表请求参数
const requestParams = ref({
  page: 1,
  pageSize: 10,
  keyword: ''
});

// 格式化日期
const formatDate = (dateStr: string) => {
  return formatPostDate(dateStr);
};

const fetchPosts = async () => {
  // 如果已经有请求正在进行，跳过本次请求
  if (isRequestPending.value) {
    return;
  }
  
  try {
    isRequestPending.value = true;
    error.value = false;
    loading.value = true;
    const params = {
      ...requestParams.value,
      page: currentPage.value,
      pageSize: pageSize.value,
      keyword: searchKeyword.value
    };
    
    console.log('Fetching posts with params:', params);
    
    const response = await postList(params);
    
    console.log('Response received:', response);
    
    // 根据控制台输出，API 直接返回了 {total: 6, records: Array(6) } 格式
    // 没有 code 和 msg 字段，也没有包装在 data 字段中
    // 使用安全的方式检查属性
    if (response && 
        Object.prototype.hasOwnProperty.call(response, 'records') && 
        Object.prototype.hasOwnProperty.call(response, 'total')) {
      // 直接使用返回的数据
      totalRecords.value = response.total;
      posts.value = response.records || [];
    } else {
      console.error('Response error or invalid format:', response);
      ElMessage.error('获取帖子列表失败：数据格式不符合预期');
    }
  } catch (err: any) {
    console.error('获取帖子列表失败:', err);
    
    error.value = true;
    
    // 更详细的错误信息
    if (err.response) {
      // 服务器响应了错误状态码
      console.error(`HTTP ${err.response.status}: ${err.response.statusText}`);
      
      if (err.response.status === 403) {
        errorMessage.value = '权限不足，可能需要登录或令牌已过期，请重新登录';
        // 提示用户重新登录
        ElMessage.error('权限不足，请重新登录');
      } else {
        errorMessage.value = `服务器错误: ${err.response.status}`;
      }
    } else if (err.request) {
      // 请求已发出但没有收到响应
      console.error('请求已发出但未收到响应:', err.request);
      errorMessage.value = '网络连接失败，请检查网络';
    } else {
      // 其他错误
      console.error('请求配置错误:', err.message);
      errorMessage.value = '请求配置错误';
    }
    
    // 设置错误消息后显示错误状态
    ElMessage.error(errorMessage.value || '获取帖子列表失败');
  } finally {
    loading.value = false;
    isRequestPending.value = false;
  }
};

// 每页大小改变时的回调
const handleSizeChange = (newSize: number) => {
  pageSize.value = newSize;
  currentPage.value = 1; // 重置为第一页
  
  fetchPosts();
};

// 页码改变时的回调
const handleCurrentChange = (newPage: number) => {
  currentPage.value = newPage;
  
  fetchPosts();
};

// 处理搜索
const handleSearch = () => {
  currentPage.value = 1; // 搜索时回到第一页
  
  fetchPosts();
};

// 处理点赞/取消点赞
const toggleLike = async (post: PostItem) => {
  try {
    // 如果用户未登录，触发需要登录事件
    if (!store.getters.isAuthenticated) {
      emit('need-login');
      return;
    }

    // 调用API进行点赞/取消点赞
    const response = await likePost(post.id);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await fetchPosts();
      ElMessage.success(post.isLiked ? '已取消点赞' : '已点赞');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('点赞操作失败:', error);
    ElMessage.error('点赞操作失败，请稍后再试');
  }
};

// 处理收藏/取消收藏
const toggleFavorite = async (post: PostItem) => {
  try {
    // 如果用户未登录，触发需要登录事件
    if (!store.getters.isAuthenticated) {
      emit('need-login');
      return;
    }

    // 调用API进行收藏/取消收藏
    const response = await favoritePost(post.id);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await fetchPosts();
      ElMessage.success(post.isFavorited ? '已取消收藏' : '已收藏');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('收藏操作失败:', error);
    ElMessage.error('收藏操作失败，请稍后再试');
  }
};

// 处理关注/取消关注
const toggleFollow = async (post: PostItem) => {
  try {
    // 如果用户未登录，触发需要登录事件
    if (!store.getters.isAuthenticated) {
      emit('need-login');
      return;
    }

    // 调用API进行关注/取消关注
    const response = await followUser(post.userId);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await fetchPosts();
      ElMessage.success(post.isFollowed ? '已取消关注' : '已关注');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('关注操作失败:', error);
    ElMessage.error('关注操作失败，请稍后再试');
  }
};

// 处理分享
const handleShare = (post: PostItem) => {
  // 创建分享链接
  const shareUrl = `${window.location.origin}/post/${post.id}`;
  const shareText = `推荐这篇帖子：${post.title}`;

  // 尝试使用 Web Share API
  if (navigator.share) {
    navigator.share({
      title: post.title,
      text: shareText,
      url: shareUrl
    }).catch(console.error);
  } else {
    // 如果不支持 Web Share API，则复制链接到剪贴板
    navigator.clipboard.writeText(shareUrl).then(() => {
      ElMessage.success('链接已复制到剪贴板');
    }).catch(console.error);
  }
};

// 确认删除帖子
const confirmDeletePost = async (postId: number) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个帖子吗？此操作不可撤销。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除帖子的API
    const response = await deletePost(postId);
    
    if (response.code === 1) {
      ElMessage.success('帖子删除成功');
      // 重新加载帖子列表
      await fetchPosts();
    } else {
      ElMessage.error(response.msg || '删除帖子失败');
    }
  } catch (error) {
    // 用户取消删除或发生错误
    if (error !== 'cancel') {
      console.error('删除帖子失败:', error);
      ElMessage.error('删除帖子失败');
    }
  }
};

// 打开编辑模态框
const openEditModal = (postId: number) => {
  // 先重置postId，然后在下一个tick设置新值，确保watch触发
  editingPostId.value = null;
  nextTick(() => {
    editingPostId.value = postId;
    editDialogVisible.value = true;
  });
};

// 编辑成功后的回调
const onEditSuccess = () => {
  editDialogVisible.value = false;
  editingPostId.value = null;
  fetchPosts(); // 重新加载帖子列表
};

// 模态框关闭回调
const onModalClose = () => {
  editingPostId.value = null; // 重置postId
};

// 页面加载
onMounted(() => {
  fetchPosts();
});

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_empty = _resolveComponent("el-empty")!
  const _component_el_alert = _resolveComponent("el-alert")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_el_input, {
          modelValue: searchKeyword.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchKeyword).value = $event)),
          placeholder: "搜索帖子...",
          class: "search-input",
          onInput: handleSearch,
          clearable: ""
        }, null, 8, ["modelValue"]),
        _createVNode(_component_el_button, {
          type: "primary",
          icon: "Search",
          class: "search-button",
          onClick: handleSearch
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode(" 搜索 ", -1)
          ]))]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(posts.value, (post) => {
        return (_openBlock(), _createElementBlock("div", {
          key: post.id,
          class: "post-card",
          onClick: ($event: any) => (goToPostDetail(post.id)),
          style: {"cursor":"pointer"}
        }, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_component_el_avatar, {
              size: 40,
              src: post.userAvatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
              class: "author-avatar"
            }, null, 8, ["src"]),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("h4", null, _toDisplayString(post.name), 1),
              _createElementVNode("span", _hoisted_8, _toDisplayString(formatDate(post.createTime)), 1)
            ]),
            _createElementVNode("div", _hoisted_9, [
              (computedUserId.value !== post.userId)
                ? (_openBlock(), _createBlock(_component_el_button, {
                    key: 0,
                    type: post.isFollowed ? 'success' : 'default',
                    size: "small",
                    class: "follow-btn",
                    onClick: _withModifiers(($event: any) => (toggleFollow(post)), ["stop"])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(post.isFollowed ? '已关注' : '+ 关注'), 1)
                    ]),
                    _: 2
                  }, 1032, ["type", "onClick"]))
                : _createCommentVNode("", true)
            ])
          ]),
          (post.coverImage)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("img", {
                  src: post.coverImage,
                  alt: "帖子封面"
                }, null, 8, _hoisted_11)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("h3", _hoisted_13, _toDisplayString(post.title), 1),
            _createElementVNode("p", _hoisted_14, _toDisplayString(post.content), 1)
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createVNode(_component_el_button, {
              icon: _unref(ThumbUpIcon),
              size: "small",
              type: post.isLiked ? 'danger' : 'default',
              class: "action-btn",
              onClick: _withModifiers(($event: any) => (toggleLike(post)), ["stop"])
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(post.isLiked ? '已点赞' : '点赞') + " (" + _toDisplayString(post.likeCount) + ") ", 1)
              ]),
              _: 2
            }, 1032, ["icon", "type", "onClick"]),
            _createVNode(_component_el_button, {
              icon: _unref(ChatLineRoundIcon),
              size: "small",
              type: "default",
              class: "action-btn",
              onClick: _withModifiers(($event: any) => (goToPostDetail(post.id)), ["stop"])
            }, {
              default: _withCtx(() => [
                _createTextVNode(" 评论 (" + _toDisplayString(post.commentCount) + ") ", 1)
              ]),
              _: 2
            }, 1032, ["icon", "onClick"]),
            _createVNode(_component_el_button, {
              icon: _unref(StarIcon),
              size: "small",
              type: post.isFavorited ? 'warning' : 'default',
              class: "action-btn",
              onClick: _withModifiers(($event: any) => (toggleFavorite(post)), ["stop"])
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(post.isFavorited ? '已收藏' : '收藏') + " (" + _toDisplayString(post.favoriteCount) + ") ", 1)
              ]),
              _: 2
            }, 1032, ["icon", "type", "onClick"]),
            _createVNode(_component_el_button, {
              icon: _unref(ShareIcon),
              size: "small",
              type: "default",
              class: "action-btn",
              onClick: _withModifiers(($event: any) => (handleShare(post)), ["stop"])
            }, {
              default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode(" 分享 ", -1)
              ]))]),
              _: 1
            }, 8, ["icon", "onClick"]),
            (computedUserId.value === post.userId)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createVNode(_component_el_button, {
                    size: "small",
                    type: "primary",
                    class: "action-btn edit-btn",
                    onClick: _withModifiers(($event: any) => (openEditModal(post.id)), ["stop"])
                  }, {
                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                      _createTextVNode(" 编辑 ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["onClick"]),
                  _createVNode(_component_el_button, {
                    size: "small",
                    type: "danger",
                    class: "action-btn delete-btn",
                    onClick: _withModifiers(($event: any) => (confirmDeletePost(post.id)), ["stop"])
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode(" 删除 ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["onClick"])
                ]))
              : _createCommentVNode("", true)
          ]),
          (post.hotComment)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                _createElementVNode("div", _hoisted_18, [
                  _createVNode(_component_el_avatar, {
                    size: 24,
                    src: post.hotComment.userAvatar,
                    class: "comment-author-avatar"
                  }, null, 8, ["src"]),
                  _createElementVNode("span", _hoisted_19, _toDisplayString(post.hotComment.name), 1)
                ]),
                _createElementVNode("p", _hoisted_20, _toDisplayString(post.hotComment.content), 1),
                _createElementVNode("span", _hoisted_21, _toDisplayString(formatDate(post.hotComment.createTime)), 1)
              ]))
            : _createCommentVNode("", true)
        ], 8, _hoisted_5))
      }), 128)),
      _createElementVNode("div", _hoisted_22, [
        _createVNode(_component_el_pagination, {
          "current-page": currentPage.value,
          "onUpdate:currentPage": _cache[1] || (_cache[1] = ($event: any) => ((currentPage).value = $event)),
          "page-size": pageSize.value,
          "onUpdate:pageSize": _cache[2] || (_cache[2] = ($event: any) => ((pageSize).value = $event)),
          "page-sizes": [5, 10, 20, 50],
          total: totalRecords.value,
          layout: "total, sizes, prev, pager, next, jumper",
          onSizeChange: handleSizeChange,
          onCurrentChange: handleCurrentChange
        }, null, 8, ["current-page", "page-size", "total"])
      ])
    ]),
    _createVNode(EditPostModal, {
      modelValue: editDialogVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((editDialogVisible).value = $event)),
      "post-id": editingPostId.value,
      onSaveSuccess: onEditSuccess,
      onClose: onModalClose
    }, null, 8, ["modelValue", "post-id"]),
    (posts.value.length === 0 && !loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
          _createVNode(_component_el_empty, { description: "暂无帖子，快发布第一个帖子吧！" })
        ]))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
          _createVNode(_component_el_alert, {
            title: "加载失败",
            type: "error",
            description: errorMessage.value,
            "show-icon": "",
            closable: ""
          }, null, 8, ["description"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})